import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useReactToPrint } from "react-to-print";
// import html2pdf from "html2pdf.js";
import { isEqual, isEmpty } from "lodash";

import { useStateSelector } from "utils/field";
import { assemblePackageResults } from "utils/results/runIdentity";
import { firestore, MODEL_COLL } from "_firebase";
import { actions as modelActions } from "features/Model/_ducks";
import { actions as resultsActions } from "features/Model/Review/Results/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { actions as userActions } from "store/users";

import { selectResults, selectResultsError } from "features/Model/Review/Results/_ducks/selectors";
import { selectUpgradePackages } from "features/Model/Upgrades/_ducks/selectors";
import { selectNzStudyReportPdfData } from "features/Model/_ducks/selectors";
import { selectAllImages } from "store/imageGallery/selectors";
import { selectUserModelDir } from "store/users/selectors";

import DrawerComponent from "components/Drawer";
import Select from "components/Input/Select";
import Input from "components/Input";
import Textarea from "components/Input/Textarea";
import Button from "components/Button";
import ImageSelect from "components/Input/ImageSelect";
import Dialog from "components/Dialog";
import { CloseDialog } from "components/Dialog";

import NZReportPDF from "./NZReportPDF";
import SelectEFileModel from "./SelectEFileModel";
import TopLevelContent from "./AutofilledInputs/TopLevelContent";
import UpgradesIncluded from "./AutofilledInputs/UpgradesIncluded";
import NetZeroPlanTable from "./AutofilledInputs/NetZeroPlanTable";
import EnergySummary from "./AutofilledInputs/EnergySummary";
import SnackbarAlert from "components/Alert";
import PageInputContainer from "components/PdfExport/PageInputContainer";
import SplitPagesContainer from "components/PdfExport/SplitPagesContainer";

import Delete from "assets/images/icons/JSX/Delete";
import CloseIcon from "assets/images/icons/closeCircle.svg";

import classes from "./styles.module.scss";
import FloatingCloseButton from "components/FloatingCloseButton";

const { updateNzStudyPdfData, getModelInfo } = modelActions;
const { getResults } = resultsActions;
const { getUpgrades } = upgradeActions;
const { fetchUserDir } = userActions;

const ExportNZCaseStudyPDFDrawer = ({
    isOpen,
    onClose,
    upgradePackages,
    nzStudyReportPdfData = {},
    updateNzStudyPdfData,
    allImages,
    results,
    resultsError,
    getResults,
    getUpgrades,
    modelDir,
    fetchUserDir,
    uid,
    getModelInfo,
    handleSave = () => {},
}) => {
    const pdfRef = useRef(null);
    const hasFetchedEFileResultsRef = useRef(false);
    const hasFetchedEFileDataRef = useRef(false);

    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [isSelectEFileOpen, setIsSelectEFileOpen] = useState(false);
    const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
    const [isLoadingEFile, setIsLoadingEFile] = useState(false);
    const [eFileError, setEFileError] = useState(null);
    // Store eFileResults, eFileModelData, and eFileUpgradePackages in local state instead of Redux to avoid large objects in modelData
    const [eFileResults, setEFileResults] = useState({});
    const [eFileModelData, setEFileModelData] = useState({});
    const [eFileUpgradePackages, setEFileUpgradePackages] = useState({});

    const {
        selectedUpgrade = "",
        projectIntroduction = "",
        projectPriorities = [""],
        beforeComfort = "",
        afterComfort = "",
        beforeBuildingCondition = "",
        afterBuildingCondition = "",
        beforeElectricalPanelSize = "",
        afterElectricalPanelSize = "",
        estimatedUpfront = 0,
        carbonPayback = 0,
        whyAreWeRetrofittingOurHome = "",
        whatLessonsDidWeLearn = "",
        whatElseNeedsToBeDone = "",
        preConstructionImages = [],
        midConstructionImages = [],
        postConstructionImages = [],
        technicalDetailsImages = [],
        pdfCoverImage = [],
        eFileModelId = "",
        eFileModelName = "",
    } = nzStudyReportPdfData;

    const { modelId, modelDetails: { lastCliRun: { cliId: cliIds = {} } = {} } = {} } = useStateSelector("model") || {};

    const { packageResults = {}, loading: resultsLoading = false } = results || {};
    const { base: { metadata: { mode } = {} } = {} } = packageResults || {};
    const hasResults = !!mode;

    const { models = {}, folders = {} } = modelDir || {};

    // Initialize eFileResults, eFileModelData, and eFileUpgradePackages from Redux state if they exist (for backward compatibility)
    useEffect(() => {
        if (nzStudyReportPdfData.eFileResults && Object.keys(nzStudyReportPdfData.eFileResults).length > 0) {
            setEFileResults(nzStudyReportPdfData.eFileResults);
            hasFetchedEFileResultsRef.current = true;
        }
        if (nzStudyReportPdfData.eFileModelData && Object.keys(nzStudyReportPdfData.eFileModelData).length > 0) {
            setEFileModelData(nzStudyReportPdfData.eFileModelData);
        }
        if (
            nzStudyReportPdfData.eFileUpgradePackages &&
            Object.keys(nzStudyReportPdfData.eFileUpgradePackages).length > 0
        ) {
            setEFileUpgradePackages(nzStudyReportPdfData.eFileUpgradePackages);
        }
        if (
            eFileModelId &&
            (Object.keys(nzStudyReportPdfData.eFileResults || {}).length > 0 ||
                Object.keys(nzStudyReportPdfData.eFileModelData || {}).length > 0 ||
                Object.keys(nzStudyReportPdfData.eFileUpgradePackages || {}).length > 0)
        ) {
            lastFetchedModelIdRef.current = eFileModelId;
            hasFetchedEFileDataRef.current = true;
        }
    }, []); // Only run once on mount

    // Track the last eFileModelId we fetched for
    const lastFetchedModelIdRef = useRef(null);

    // Re-fetch eFileResults, eFileModelData, and eFileUpgradePackages when component opens if eFileModelId exists but data is empty
    useEffect(() => {
        if (!isOpen || !eFileModelId) {
            return;
        }

        // If we've already fetched for this modelId, don't fetch again
        if (hasFetchedEFileDataRef.current && lastFetchedModelIdRef.current === eFileModelId) {
            return;
        }

        // If modelId changed, reset the flag
        if (lastFetchedModelIdRef.current !== eFileModelId) {
            hasFetchedEFileDataRef.current = false;
            hasFetchedEFileResultsRef.current = false;
        }

        if (hasFetchedEFileDataRef.current) {
            return;
        }

        const fetchEFileData = async () => {
            setIsLoadingEFile(true);
            setEFileError(null);

            try {
                // Fetch model info to get cliIds and modelData
                const modelInfo = await getModelInfo(eFileModelId);
                if (!modelInfo || !modelInfo.modelDetails || !modelInfo.modelDetails.lastCliRun) {
                    setEFileError("Selected model has no results");
                    setIsLoadingEFile(false);
                    hasFetchedEFileDataRef.current = true; // Mark as fetched even on error to avoid retries
                    lastFetchedModelIdRef.current = eFileModelId;
                    return;
                }

                // Fetch upgrade packages
                const upgradesDoc = await firestore
                    .doc(`${MODEL_COLL}/${eFileModelId}`)
                    .collection("modelData")
                    .doc("upgrades")
                    .get();
                const eFilePackages = upgradesDoc.data() || {};

                // Store upgrade packages and model data in local state
                setEFileUpgradePackages(eFilePackages);
                setEFileModelData(modelInfo.modelData || {});

                const { cliId = {} } = modelInfo.modelDetails.lastCliRun;
                if (isEmpty(cliId)) {
                    setEFileError("Selected model has no results");
                    setIsLoadingEFile(false);
                    hasFetchedEFileDataRef.current = true; // Mark as fetched even on error to avoid retries
                    lastFetchedModelIdRef.current = eFileModelId;
                    return;
                }

                const runIds = Object.values(cliId).map(({ runId = "" }) => runId);

                // Fetch results directly using getCliResults utility
                const { getCliResults } = await import("utils/results/api");
                const resultsArray = await Promise.all(
                    runIds.map((cliId) => getCliResults({ cliId, modelId: eFileModelId }))
                );

                const resultData = assemblePackageResults(resultsArray.map(({ data }) => data));

                if (isEmpty(resultData) || !resultData.base) {
                    setEFileError("Selected model has no results");
                    setIsLoadingEFile(false);
                    hasFetchedEFileDataRef.current = true; // Mark as fetched even on error to avoid retries
                    lastFetchedModelIdRef.current = eFileModelId;
                    return;
                }

                // Success - store results in local state
                setEFileResults(resultData);
                hasFetchedEFileResultsRef.current = true;
                hasFetchedEFileDataRef.current = true;
                lastFetchedModelIdRef.current = eFileModelId;
                setIsLoadingEFile(false);
            } catch (error) {
                console.error("Error loading E-file data:", error);
                setEFileError("Failed to load E-file data");
                setIsLoadingEFile(false);
                hasFetchedEFileDataRef.current = true; // Mark as fetched even on error to avoid retries
                lastFetchedModelIdRef.current = eFileModelId;
            }
        };

        fetchEFileData();
    }, [isOpen, eFileModelId, getModelInfo]); // Only depend on isOpen, eFileModelId, and getModelInfo

    // Reset fetch flag when drawer closes
    useEffect(() => {
        if (!isOpen) {
            hasFetchedEFileResultsRef.current = false;
            hasFetchedEFileDataRef.current = false;
        }
    }, [isOpen]);

    // Fetch user directory on mount
    useEffect(() => {
        if (uid && isOpen) {
            fetchUserDir(uid, false);
        }
    }, [uid, isOpen, fetchUserDir]);

    useEffect(() => {
        if (!resultsLoading && !hasResults && Object.keys(cliIds).length > 0 && resultsError !== "Results not found.") {
            const runIds = Object.values(cliIds).map(({ runId = "" }) => runId);
            getResults({ cliIds: runIds, modelId });
        }
    }, [cliIds, hasResults, resultsError, resultsLoading, modelId]);

    useEffect(() => {
        if (!selectedUpgrade) return;

        const isEFileUpgrade = selectedUpgrade.startsWith("eFile_");
        const actualUpgradeKey = isEFileUpgrade ? selectedUpgrade.replace("eFile_", "") : selectedUpgrade;

        if (isEFileUpgrade) {
            // Don't clear selectedUpgrade if we need to fetch E-file data but haven't done so yet
            // This uses the same logic as the fetch check to prevent clearing during re-fetch
            const needsFetch =
                eFileModelId && (!hasFetchedEFileDataRef.current || lastFetchedModelIdRef.current !== eFileModelId);

            if (needsFetch) {
                // We're about to fetch or currently fetching, don't clear the selection
                return;
            }

            // Check E-file results only after we've finished fetching (or determined we don't need to)
            if (!eFileResults || !eFileResults[actualUpgradeKey]) {
                updateNzStudyPdfData("selectedUpgrade", "");
            }
        } else {
            // Check current model results
            if (!packageResults || !packageResults[selectedUpgrade]) {
                updateNzStudyPdfData("selectedUpgrade", "");
            }
        }
    }, [selectedUpgrade, updateNzStudyPdfData, results, eFileResults, packageResults, eFileModelId]);

    // Handle E-file model selection
    const handleEFileModelSelect = async (selectedModelId, modelInfo) => {
        setIsLoadingEFile(true);
        setEFileError(null);

        try {
            // First fetch upgrade packages
            const upgradesDoc = await firestore
                .doc(`${MODEL_COLL}/${selectedModelId}`)
                .collection("modelData")
                .doc("upgrades")
                .get();
            const eFilePackages = upgradesDoc.data() || {};

            // if (isEmpty(eFilePackages) || Object.keys(eFilePackages).length === 0) {
            //     setEFileError("Selected model has no upgrade packages");
            //     setIsLoadingEFile(false);
            //     return;
            // }

            // Get cliIds from modelInfo
            const { cliId = {} } = modelInfo?.modelDetails?.lastCliRun || {};
            if (isEmpty(cliId)) {
                setEFileError("Selected model has no results");
                setIsLoadingEFile(false);
                return;
            }

            const runIds = Object.values(cliId).map(({ runId = "" }) => runId);

            // Fetch results directly using getCliResults utility
            const { getCliResults } = await import("utils/results/api");
            const resultsArray = await Promise.all(
                runIds.map((cliId) => getCliResults({ cliId, modelId: selectedModelId }))
            );

            const resultData = assemblePackageResults(resultsArray.map(({ data }) => data));

            if (isEmpty(resultData) || !resultData.base) {
                setEFileError("Selected model has no results");
                setIsLoadingEFile(false);
                return;
            }

            // Success - store E-file data
            // Store eFileResults, eFileModelData, and eFileUpgradePackages in local state instead of Redux
            setEFileResults(resultData);
            setEFileUpgradePackages(eFilePackages);
            setEFileModelData(modelInfo.modelData || {});
            hasFetchedEFileResultsRef.current = true;
            hasFetchedEFileDataRef.current = true;
            lastFetchedModelIdRef.current = selectedModelId;
            updateNzStudyPdfData("eFileModelId", selectedModelId);
            updateNzStudyPdfData("eFileModelName", models[selectedModelId]?.name || "");

            // Clear selected upgrade if it doesn't exist in new packages
            if (selectedUpgrade && !eFilePackages[selectedUpgrade] && !upgradePackages[selectedUpgrade]) {
                updateNzStudyPdfData("selectedUpgrade", "");
            }

            setIsLoadingEFile(false);
        } catch (error) {
            console.error("Error loading E-file:", error);
            setEFileError("Failed to load E-file data");
            setIsLoadingEFile(false);
        }
    };

    const handleClearEFile = () => {
        setIsClearConfirmOpen(true);
    };

    const confirmClearEFile = () => {
        // Check if selected upgrade exists in current model
        const upgradeExistsInCurrent = selectedUpgrade && upgradePackages[selectedUpgrade];

        // Clear E-file data
        // Clear eFileResults, eFileModelData, and eFileUpgradePackages from local state
        setEFileResults({});
        setEFileModelData({});
        setEFileUpgradePackages({});
        hasFetchedEFileResultsRef.current = false;
        hasFetchedEFileDataRef.current = false;
        lastFetchedModelIdRef.current = null;
        updateNzStudyPdfData("eFileModelId", "");
        updateNzStudyPdfData("eFileModelName", "");

        // Clear selected upgrade if it doesn't exist in current model
        if (!upgradeExistsInCurrent) {
            updateNzStudyPdfData("selectedUpgrade", "");
        }

        setIsClearConfirmOpen(false);
        setEFileError(null);
    };

    // Combine upgrade packages from current model and E-file
    const allUpgradePackages = useMemo(() => {
        const combined = { ...upgradePackages };
        if (!isEmpty(eFileUpgradePackages)) {
            Object.keys(eFileUpgradePackages)
                .filter((key) => key !== "base")
                .forEach((key) => {
                    combined[`eFile_${key}`] = eFileUpgradePackages[key];
                });
        }
        return combined;
    }, [upgradePackages, eFileUpgradePackages]);

    // Combine results from current model and E-file
    const allResults = useMemo(() => {
        const combined = { ...packageResults };
        if (!isEmpty(eFileResults)) {
            Object.keys(eFileResults).forEach((key) => {
                combined[`eFile_${key}`] = eFileResults[key];
            });
        }
        return combined;
    }, [packageResults, eFileResults]);

    const upgradePackagesLabelsOptions = useMemo(() => {
        const currentModelOptions = Object.keys(upgradePackages)
            .filter((key) => key !== "base")
            .filter((key) => results?.packageResults[key])
            .filter((key) => Object.keys(results?.packageResults?.[key]?.upgradePackage || {}).length > 0)
            .map((key) => ({ label: `${upgradePackages[key].name} (Current Model)`, value: key }));

        const eFileOptions =
            !isEmpty(eFileUpgradePackages) && !isEmpty(eFileResults)
                ? Object.keys(eFileUpgradePackages)
                      //   .filter((key) => key !== "base")
                      .filter((key) => eFileResults[key])
                      .filter((key) => Object.keys(eFileResults[key]?.upgradePackage || {}).length > 0)
                      .filter((key) =>
                          Object.values(eFileResults[key]?.upgradePackage).some(
                              (upgradePackage) => !isEmpty(upgradePackage?.upgrades)
                          )
                      )
                      .map((key) => ({
                          label: `${eFileUpgradePackages[key].name} (E-file)`,
                          value: `eFile_${key}`,
                      }))
                : [];

        const eFileBaseCase = Object.keys(eFileResults).includes("base")
            ? [{ label: "Base Case (E-file)", value: "eFile_base" }]
            : [];
        return [...currentModelOptions, ...eFileBaseCase, ...eFileOptions];
    }, [upgradePackages, eFileUpgradePackages, results?.packageResults, eFileResults]);

    const pre_construction_images = useMemo(
        () =>
            allImages
                .filter((image) => image.status === "pre_construction")
                .map(({ id, fileUrl, name, notes }) => ({ value: id, label: name, image: fileUrl, notes })),
        [allImages]
    );
    const mid_construction_images = useMemo(
        () =>
            allImages
                .filter((image) => image.status === "mid_construction")
                .map(({ id, fileUrl, name, notes }) => ({ value: id, label: name, image: fileUrl, notes })),
        [allImages]
    );
    const post_construction_images = useMemo(
        () =>
            allImages
                .filter((image) => image.status === "post_construction")
                .map(({ id, fileUrl, name, notes }) => ({ value: id, label: name, image: fileUrl, notes })),
        [allImages]
    );

    const all_gallery_images = useMemo(
        () => allImages.map(({ id, fileUrl, name, notes }) => ({ value: id, label: name, image: fileUrl, notes })),
        [allImages]
    );

    useEffect(() => {
        // Filter out selected images that are no longer included in the option list (changes to status after selecting image)
        const pre_options = pre_construction_images.reduce((acc, cur) => [...acc, cur.value], []);
        const mid_options = mid_construction_images.reduce((acc, cur) => [...acc, cur.value], []);
        const post_options = post_construction_images.reduce((acc, cur) => [...acc, cur.value], []);

        const filteredPreImages = preConstructionImages.filter((val) => pre_options.includes(val));
        const filteredMidImages = midConstructionImages.filter((val) => mid_options.includes(val));
        const filteredPostImages = postConstructionImages.filter((val) => post_options.includes(val));

        if (!isEqual(filteredPreImages, preConstructionImages)) {
            updateNzStudyPdfData("preConstructionImages", filteredPreImages);
        }
        if (!isEqual(filteredMidImages, midConstructionImages)) {
            updateNzStudyPdfData("midConstructionImages", filteredMidImages);
        }
        if (!isEqual(filteredPostImages, postConstructionImages)) {
            updateNzStudyPdfData("postConstructionImages", filteredPostImages);
        }
    }, [allImages, updateNzStudyPdfData]);

    const printHtmlToPdf = useReactToPrint({
        content: () => pdfRef.current,
        documentTitle: `nz_report.pdf`,
        pageStyle: `
            @media print {
                body {
                    -webkit-print-color-adjust: exact;
                    color-adjust: exact;
                }
                @page {
                    size: 8.5in 11in;
                    margin: 0;
                }
            }
        `,
        copyStyles: true,
        onBeforePrint: () => {
            setIsGeneratingPdf(true);
            handleSave();
        },
        // Issues with text alignment in html2pdf, use react-to-print's default print function
        // print: async (printIframe) => {
        //     setIsGeneratingPdf(true);

        //     const document = await printIframe.contentDocument;

        //     if (document) {
        //         const html = await document.getElementById("nzPdfPrint");

        //         const opt = {
        //             filename: `nz_report.pdf`,
        //             image: { type: "jpeg", quality: 1 },
        //             html2canvas: { dpi: 120, scale: 2 },
        //             jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
        //         };

        //         await html2pdf().from(html).set(opt).save();

        //         setIsGeneratingPdf(false);
        //     }

        //     setIsGeneratingPdf(false);
        // },
        onAfterPrint: () => {
            setIsGeneratingPdf(false);
        },
    });

    return (
        <DrawerComponent
            open={isOpen}
            close={onClose}
            title="Export PDF"
            classes={{
                paperClass: classes.drawerContainer,
                rootClass: classes.rootDrawerContainer,
            }}
            bodyClass={classes.drawerBody}
        >
            <SnackbarAlert
                open={!selectedUpgrade}
                message={"No upgrade package selected."}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                direction="down"
                severity="error"
                onExited={() => {}}
                top={"60px"}
            />
            <FloatingCloseButton
                tooltipId="nz_case_study_pdf_close"
                tooltipText="Changes will be saved"
                handleClose={onClose}
            />
            <div className={classes.inputsContainer}>
                <div className={classes.upgradePackageRow}>
                    <div className={classes.upgradePackageSelectColumn}>
                        <Select
                            options={upgradePackagesLabelsOptions}
                            input={{
                                value: selectedUpgrade,
                                onChange: (value) => updateNzStudyPdfData("selectedUpgrade", value),
                            }}
                            meta={{ invalid: !selectedUpgrade, error: "This field is required" }}
                            label={"Select Upgrade Package (With PV)"}
                            disabled={isLoadingEFile}
                            placeholder={isLoadingEFile ? "Loading options..." : "Select upgrade package"}
                            // warning={selectedUpgrade === "eFile_base" ? "chbaNzEfileBaseUpgradesIncludeWarning" : null}
                        />
                    </div>
                    <div className={classes.upgradePackageButtonColumn}>
                        <p className={classes.upgradePackageButtonDescription}>
                            (Optional) Select a different model if your upgrades were modelled separately from your main
                            model.
                        </p>
                        <Button
                            onClick={() => {
                                setIsSelectEFileOpen(true);
                            }}
                            type="hollow"
                            style={{ width: "100%" }}
                        >
                            Select E-file or External Model
                        </Button>
                    </div>
                    <div className={classes.upgradePackageInfoColumn}>
                        {!isLoadingEFile &&
                            (eFileModelId ? (
                                <div className={classes.eFileInfo}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <p className={classes.eFileModelName}>{eFileModelName}</p>
                                        <button
                                            onClick={handleClearEFile}
                                            className={classes.clearEFileButton}
                                            title="Clear E-file selection"
                                        >
                                            <img
                                                src={CloseIcon}
                                                alt="Clear"
                                                style={{ width: "16px", height: "16px" }}
                                            />
                                        </button>
                                    </div>
                                    <p className={classes.eFilePackageCount}>
                                        {
                                            Object.keys(eFileUpgradePackages).filter(
                                                (key) =>
                                                    key !== "base" &&
                                                    eFileResults[key] &&
                                                    !isEmpty(eFileResults[key]?.upgradePackage) &&
                                                    Object.values(eFileResults[key]?.upgradePackage).some(
                                                        (upgradePackage) => !isEmpty(upgradePackage?.upgrades)
                                                    )
                                            ).length
                                        }{" "}
                                        upgrade package
                                        {Object.keys(eFileUpgradePackages).filter(
                                            (key) =>
                                                key !== "base" &&
                                                eFileResults[key] &&
                                                !isEmpty(eFileResults[key]?.upgradePackage) &&
                                                Object.values(eFileResults[key]?.upgradePackage).some(
                                                    (upgradePackage) => !isEmpty(upgradePackage?.upgrades)
                                                )
                                        ).length !== 1
                                            ? "s"
                                            : ""}{" "}
                                        with results
                                    </p>
                                </div>
                            ) : (
                                <p className={classes.noEFileText}>No E-file selected</p>
                            ))}

                        {eFileError && <p className={classes.eFileError}>{eFileError}</p>}
                        {isLoadingEFile && <p className={classes.eFileLoading}>Loading E-file...</p>}
                    </div>
                </div>
                <div style={{ height: "0.0625rem", background: "#e1eaf0" }}></div>

                <PageInputContainer title={"Page 1"} contentClass={classes.page1}>
                    <div className={classes.pageInputColumn}>
                        <ImageSelect
                            label={"Cover Image"}
                            options={all_gallery_images.map((image) => ({
                                ...image,
                                value: `${image.value}-cover`,
                            }))}
                            input={{
                                value: pdfCoverImage,
                                onChange: (value) => updateNzStudyPdfData("pdfCoverImage", value),
                            }}
                            dimensions={{ width: "225px", height: "200px", isWideThreshold: 100, crop: true }}
                            selectOne
                        />
                        <Textarea
                            label={"Project Introduction"}
                            value={projectIntroduction}
                            minHeight={"100px"}
                            onChange={(e) =>
                                updateNzStudyPdfData("projectIntroduction", e.target.value.slice(0, 300))
                            }
                            withLimit={true}
                            limit={300}
                        />
                        <div className={classes.prioritiesContainer}>
                            <span className={classes.prioritiesLabel}>Project Priorities</span>
                            <div className={classes.prioritiesWrapper}>
                                {projectPriorities.map((priority, index) => (
                                    <div key={index} className={classes.priorityContainer}>
                                        <Input
                                            input={{
                                                value: priority,
                                                onChange: (value) => {
                                                    const newPriorities = [...projectPriorities];

                                                    newPriorities[index] = value.slice(0, 50);

                                                    updateNzStudyPdfData("projectPriorities", newPriorities);
                                                },
                                            }}
                                            placeholder="Enter project priority"
                                            withLimit={true}
                                            limit={50}
                                            hideLabel
                                        />
                                        <div
                                            onClick={() =>
                                                updateNzStudyPdfData(
                                                    "projectPriorities",
                                                    projectPriorities.filter((_, i) => i !== index)
                                                )
                                            }
                                            style={{ cursor: "pointer" }}
                                        >
                                            <Delete />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {projectPriorities.length < 6 && (
                                <Button
                                    onClick={() =>
                                        updateNzStudyPdfData("projectPriorities", [...projectPriorities, ""])
                                    }
                                    style={{ marginTop: "10px" }}
                                >
                                    Add Priority
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={classes.pageInputColumn}>
                        <TopLevelContent eFileResults={eFileResults} />
                    </div>
                    <div className={classes.pageInputColumn}>
                        <UpgradesIncluded eFileUpgradePackages={eFileUpgradePackages} />
                    </div>
                </PageInputContainer>
                <SplitPagesContainer horizontal>
                    <PageInputContainer title={"Page 2"} contentClass={classes.page2}
                        info={"Default content was taken from the current model's data and the selected upgrade package (excluding Comfort, Building Condition, and Electrical Panel Size)."}
                    >
                        <NetZeroPlanTable
                            eFileResults={eFileResults}
                            eFileModelData={eFileModelData}
                            eFileUpgradePackages={eFileUpgradePackages}
                        />
                    </PageInputContainer>
                    <SplitPagesContainer vertical>
                        <PageInputContainer title={"Page 3"} contentClass={classes.page3}>
                            <EnergySummary eFileResults={eFileResults} eFileModelData={eFileModelData} />
                        </PageInputContainer>
                        <PageInputContainer title={"Page 4"} contentClass={classes.page4} containerStyle={{ height: "100%" }}>
                            <div className={classes.reflectionTexts}>
                                <Textarea
                                    label={"Why are We retrofitting Our Home?"}
                                    value={whyAreWeRetrofittingOurHome}
                                    minHeight={"110px"}
                                    onChange={(e) =>
                                        updateNzStudyPdfData(
                                            "whyAreWeRetrofittingOurHome",
                                            e.target.value.slice(0, 450)
                                        )
                                    }
                                    withLimit={true}
                                    limit={450}
                                    resize={"vertical"}
                                />
                                <Textarea
                                    label={"What Lessons Did We Learn?"}
                                    value={whatLessonsDidWeLearn}
                                    minHeight={"110px"}
                                    onChange={(e) =>
                                        updateNzStudyPdfData("whatLessonsDidWeLearn", e.target.value.slice(0, 450))
                                    }
                                    withLimit={true}
                                    limit={450}
                                    resize={"vertical"}
                                />
                                <Textarea
                                    label={"What Else Needs to Be Done To get To Net Zero?"}
                                    value={whatElseNeedsToBeDone}
                                    minHeight={"110px"}
                                    onChange={(e) =>
                                        updateNzStudyPdfData("whatElseNeedsToBeDone", e.target.value.slice(0, 450))
                                    }
                                    withLimit={true}
                                    limit={450}
                                    resize={"vertical"}
                                />
                            </div>
                        </PageInputContainer>
                    </SplitPagesContainer>
                </SplitPagesContainer>
                <PageInputContainer title={"Page 5"} contentClass={classes.imgPage}>
                    <ImageSelect
                        label={"Pre Construction Images"}
                        options={pre_construction_images.map((image) => ({
                            ...image,
                            disabled:
                                !preConstructionImages.includes(image.value) && preConstructionImages.length >= 4,
                        }))}
                        input={{
                            value: preConstructionImages,
                            onChange: (value) => updateNzStudyPdfData("preConstructionImages", value),
                        }}
                        info={
                            "Maximum 4 images can be selected. Notes are taken from the photo gallery and will appear on the PDF."
                        }
                        dimensions={{ width: "380px", height: "200px", isWideLimit: 0.5, crop: true }}
                        displayNotes
                        fullScreen
                    />
                </PageInputContainer>
                <PageInputContainer title={"Page 6"} contentClass={classes.imgPage}>
                    <ImageSelect
                        label={"Mid Construction Images"}
                        options={mid_construction_images.map((image) => ({
                            ...image,
                            disabled:
                                !midConstructionImages.includes(image.value) && midConstructionImages.length >= 4,
                        }))}
                        input={{
                            value: midConstructionImages,
                            onChange: (value) => updateNzStudyPdfData("midConstructionImages", value),
                        }}
                        info={
                            "Maximum 4 images can be selected. Notes are taken from the photo gallery and will appear on the PDF."
                        }
                        dimensions={{ width: "380px", height: "200px", isWideLimit: 0.5, crop: true }}
                        displayNotes
                        fullScreen
                    />
                </PageInputContainer>
                <PageInputContainer title={"Page 7"} contentClass={classes.imgPage}>
                    <ImageSelect
                        label={"Post Construction Images"}
                        options={post_construction_images.map((image) => ({
                            ...image,
                            disabled:
                                !postConstructionImages.includes(image.value) && postConstructionImages.length >= 4,
                        }))}
                        input={{
                            value: postConstructionImages,
                            onChange: (value) => updateNzStudyPdfData("postConstructionImages", value),
                        }}
                        info={
                            "Maximum 4 images can be selected. Notes are taken from the photo gallery and will appear on the PDF."
                        }
                        dimensions={{ width: "380px", height: "200px", isWideLimit: 0.5, crop: true }}
                        displayNotes
                        fullScreen
                    />
                </PageInputContainer>
                <PageInputContainer title={"Extra Pages (Optional)"} contentClass={classes.imgPage}>
                    <ImageSelect
                        label={"Technical Details"}
                        options={all_gallery_images.map((image) => ({
                            ...image,
                            value: `${image.value}-detail`,
                            disabled:
                                !technicalDetailsImages.includes(`${image.value}-detail`) &&
                                technicalDetailsImages.length >= 10,
                        }))}
                        input={{
                            value: technicalDetailsImages,
                            onChange: (value) => updateNzStudyPdfData("technicalDetailsImages", value),
                        }}
                        info={
                            "Each image selected will appear enlarged on a separate page. Descriptions are taken from notes in the photo gallery."
                        }
                        dimensions={{ width: "380px", height: "200px", isWideLimit: 0.5, crop: false }}
                        displayNotes
                        fullScreen
                    />
                </PageInputContainer>
            </div>

            {/* Export PDF Button */}
            <div className={classes.exportPdfButtonContainer}>
                <Button
                    onClick={printHtmlToPdf}
                    style={{ marginTop: "20px" }}
                    disabled={!selectedUpgrade || isGeneratingPdf}
                >
                    {isGeneratingPdf ? "Exporting..." : "Export PDF"}
                </Button>
            </div>
            {/* PDF Display */}
            {selectedUpgrade &&
                ((selectedUpgrade.startsWith("eFile_") && eFileResults[selectedUpgrade.replace("eFile_", "")]) ||
                    results?.packageResults[selectedUpgrade]) && (
                    <div className={classes.pdfPreviewContainer} style={{ paddingBottom: "100px" }}>
                        <h1 className={classes.pdfPreviewTitle}> CHBA NZ Roadmap PDF Preview </h1>
                        <NZReportPDF
                            pdfRef={pdfRef}
                            eFileModelData={eFileModelData}
                            eFileResults={eFileResults}
                            eFileUpgradePackages={eFileUpgradePackages}
                        />
                    </div>
                )
            }
            <SelectEFileModel
                isOpen={isSelectEFileOpen}
                close={() => setIsSelectEFileOpen(false)}
                models={models}
                folders={folders}
                onSelectModel={handleEFileModelSelect}
                currentModelId={modelId}
            />
            <Dialog open={isClearConfirmOpen} style={{ zIndex: 1500 }}>
                <CloseDialog closeActions={() => setIsClearConfirmOpen(false)} />
                <div className={classes.dialog}>
                    <h3>Are you sure you want to clear the E-file selection?</h3>
                    <p>
                        {selectedUpgrade && upgradePackages[selectedUpgrade]
                            ? "The selected upgrade package will be kept if it exists in the current model."
                            : "The selected upgrade package will be cleared."}
                    </p>
                    <div className={classes.dialogFooter}>
                        <Button type="hollow" onClick={() => setIsClearConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="red" onClick={confirmClearEFile}>
                            Clear E-file
                        </Button>
                    </div>
                </div>
            </Dialog>
        </DrawerComponent>
    );
};

const mapStateToProps = createStructuredSelector({
    upgradePackages: selectUpgradePackages,
    nzStudyReportPdfData: selectNzStudyReportPdfData,
    allImages: selectAllImages,
    results: selectResults,
    resultsError: selectResultsError,
    modelDir: selectUserModelDir,
    uid: (state) => state.user?.uid || "",
});

const mapDispatchToProps = (dispatch) => ({
    getResults: ({ cliIds, modelId }) => dispatch(getResults({ cliIds, modelId })),
    updateNzStudyPdfData: (fieldName, value) => dispatch(updateNzStudyPdfData(fieldName, value)),
    getUpgrades: ({ modelId }) => dispatch(getUpgrades({ modelId })),
    fetchUserDir: (uid, skipLoading) => dispatch(fetchUserDir(uid, skipLoading)),
    getModelInfo: (modelId) => dispatch(getModelInfo(modelId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExportNZCaseStudyPDFDrawer);
